/** DPR Personnel log — allowed job titles by project discipline. */

export const DPR_WELDING_PERSONNEL_ROLES = [
  'Mechanic',
  'Project QS',
  'Supervisor',
  'Welding Superintendent',
  'Senior Service Welding Technician',
  'Service Welding Technician',
  'Internal Clamp Operator',
  'External Clamp Operator',
  'Demag Operator',
  'Pre Heat Operator',
  'Welding Spacer',
  'P625 Auto Operators',
  'Manual STT/M500 Welder',
  'Welding QA/QC',
  'Production Support',
  'Bevelling Operator',
  'Lead Beveller',
  'Tracked Vehicle Driver',
];

export const DPR_COATING_PERSONNEL_ROLES = [
  'FJC Vehicle Operator',
  'Cap Dresser',
  'FJC Production Manager',
  'FJC Electro Mechanic',
  'Coating QA/QC',
  'Working Supervisor Mainline',
  'FJC Tech Mainline',
  'QC Reserved List',
  'RESERVED LIST',
];

const normalizeRoleName = (name) => String(name || '').trim().toLowerCase();

/** Allowed roles for DPR personnel picker. Welding group only for now. */
export const getDprPersonnelAllowedRoles = () => DPR_WELDING_PERSONNEL_ROLES;

/** Allowed job titles for DSR technician log (coating). */
export const getDsrTechnicianAllowedRoles = () => DPR_COATING_PERSONNEL_ROLES;

export const filterRolesByDprAllowList = (roles, allowedRoleNames) => {
  const allowed = new Set((allowedRoleNames || []).map(normalizeRoleName));
  return (roles || []).filter((role) => {
    const name = role?.roleName ?? role?.name ?? '';
    return allowed.has(normalizeRoleName(name));
  });
};

/** Merge API roles with the allow list so every permitted title appears in the picker. */
export const buildDprPersonnelRoleOptions = (roles, allowedRoleNames) => {
  const fromApi = filterRolesByDprAllowList(roles, allowedRoleNames);
  const byName = new Map();

  fromApi.forEach((role) => {
    const name = String(role?.roleName ?? role?.name ?? '').trim();
    if (name) byName.set(normalizeRoleName(name), role);
  });

  (allowedRoleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = normalizeRoleName(trimmed);
    if (!byName.has(key)) {
      byName.set(key, { roleName: trimmed, roleId: null });
    }
  });

  return [...byName.values()];
};

/**
 * Keep allow-list roles and also surface custom / in-use roles (e.g. added via POST /role).
 */
export const mergeCustomRoleOptions = ({
  baseOptions = [],
  allRoles = [],
  activeRoleNames = [],
  isPendingRole,
  getName = (role) => String(role?.roleName ?? role?.name ?? '').trim(),
}) => {
  const baseKeys = new Set((baseOptions || []).map((role) => normalizeRoleName(getName(role))));
  const extras = [];

  const addByName = (name, roleObj) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = normalizeRoleName(trimmed);
    if (baseKeys.has(key)) return;
    baseKeys.add(key);
    extras.push(roleObj ?? { roleName: trimmed, roleId: null });
  };

  (activeRoleNames || []).forEach((name) => addByName(name));

  (allRoles || []).forEach((role) => {
    const name = getName(role);
    if (!name) return;
    const key = normalizeRoleName(name);
    if (baseKeys.has(key)) return;
    if (isPendingRole?.(role) || (activeRoleNames || []).some((n) => normalizeRoleName(n) === key)) {
      addByName(name, role);
    }
  });

  return [...baseOptions, ...extras];
};

/** Build sorted string role options for DSR technician log (coating titles only). */
export const buildDsrTechnicianRoleOptions = (roleNames, allowedRoleNames, extraRoleNames = []) => {
  const allowed = new Set((allowedRoleNames || []).map(normalizeRoleName));
  const byName = new Map();

  (roleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed || !allowed.has(normalizeRoleName(trimmed))) return;
    byName.set(normalizeRoleName(trimmed), trimmed);
  });

  (allowedRoleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = normalizeRoleName(trimmed);
    if (!byName.has(key)) byName.set(key, trimmed);
  });

  (extraRoleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed || trimmed === 'Select Role') return;
    byName.set(normalizeRoleName(trimmed), trimmed);
  });

  const sorted = [...byName.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  return ['Select Role', ...sorted];
};
